import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  ViewModule as CardViewIcon,
  ViewList as ListViewIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import integrationService from '../services/integrationService'

interface Template {
  id: string
  name: string
  channel: string  // WHATSAPP, EMAIL, SMS
  language: string
  content?: string  // For EMAIL/SMS
  whatsapp_message?: string  // For WHATSAPP
  sms_message?: string  // For SMS
  variables: string[]
  usage_count?: number
  is_active: boolean
  approval_status?: string  // DRAFT, PENDING_APPROVAL, APPROVED, REJECTED, ARCHIVED
  created_at: string
  updated_at: string
}

// Helper function to get template content based on channel
const getTemplateContent = (template: Template): string => {
  if (template.channel === 'WHATSAPP' && template.whatsapp_message) {
    return template.whatsapp_message
  }
  if (template.channel === 'SMS' && template.sms_message) {
    return template.sms_message
  }
  return template.content || ''
}

const IntegrationTemplatesPage: React.FC = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterApprovalStatus, setFilterApprovalStatus] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null)

  // Load templates
  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await integrationService.getTemplates()
      setTemplates(data as unknown as Template[])
    } catch (err: any) {
      setError(err.message || 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const templateContent = getTemplateContent(template)
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      templateContent.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = filterType === 'all' || template.channel === filterType
    const matchesApproval = filterApprovalStatus === 'all' || template.approval_status === filterApprovalStatus
    return matchesSearch && matchesType && matchesApproval
  })

  // Get unique template types (channels)
  const templateTypes = ['all', ...Array.from(new Set(templates.map(t => t.channel).filter(Boolean)))]

  // Get approval status color
  const getApprovalStatusColor = (status?: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'DRAFT':
        return 'default'
      case 'PENDING_APPROVAL':
        return 'warning'
      case 'REJECTED':
        return 'error'
      case 'ARCHIVED':
        return 'info'
      default:
        return 'default'
    }
  }

  // Get template type icon
  const getTemplateIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'whatsapp':
        return <MessageIcon />
      case 'email':
        return <EmailIcon />
      case 'sms':
        return <SmsIcon />
      default:
        return <MessageIcon />
    }
  }

  // Brand-associated accent color per channel
  const getChannelHex = (type: string) => {
    switch (type.toLowerCase()) {
      case 'whatsapp':
        return '#25D366'
      case 'email':
        return '#0288d1'
      case 'sms':
        return '#7b1fa2'
      default:
        return '#616161'
    }
  }

  // Handle create template
  const handleCreate = () => {
    navigate('/admin/integrations/templates/create')
  }

  // Handle edit template
  const handleEdit = (templateId: string) => {
    navigate(`/admin/integrations/templates/${templateId}/edit`)
  }

  // Handle delete template
  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return

    try {
      await integrationService.deleteTemplate(templateToDelete.id)
      setDeleteDialogOpen(false)
      setTemplateToDelete(null)
      loadTemplates()
    } catch (err: any) {
      setError(err.message || 'Failed to delete template')
    }
  }

  // Handle preview template
  const handlePreview = (templateId: string) => {
    navigate(`/admin/integrations/templates/${templateId}/preview`)
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading templates...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h4">
          Notification Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Template
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Template Type</InputLabel>
          <Select
            value={filterType}
            label="Template Type"
            onChange={(e) => setFilterType(e.target.value)}
          >
            {templateTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type === 'all' ? 'All Types' : type.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Approval Status</InputLabel>
          <Select
            value={filterApprovalStatus}
            label="Approval Status"
            onChange={(e) => setFilterApprovalStatus(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="APPROVED">Approved</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
        >
          <ToggleButton value="card">
            <CardViewIcon />
          </ToggleButton>
          <ToggleButton value="list">
            <ListViewIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No templates found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {searchQuery || filterType !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first notification template to get started'}
          </Typography>
          {!searchQuery && filterType === 'all' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              Create Template
            </Button>
          )}
        </Box>
      ) : viewMode === 'list' ? (
        <TableContainer component={Paper} sx={{ borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Template</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Content</TableCell>
                <TableCell align="right">Variables</TableCell>
                <TableCell align="right">Used</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map((template) => {
                const accentHex = getChannelHex(template.channel)
                return (
                  <TableRow
                    key={template.id}
                    hover
                    sx={{
                      opacity: template.is_active ? 1 : 0.6,
                      borderLeft: '4px solid',
                      borderLeftColor: accentHex,
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${accentHex}1A`,
                            color: accentHex,
                            flexShrink: 0,
                            '& svg': { fontSize: 16 },
                          }}
                        >
                          {getTemplateIcon(template.channel)}
                        </Box>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {template.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={template.channel.toUpperCase()}
                        size="small"
                        sx={{ fontWeight: 600, bgcolor: `${accentHex}1A`, color: accentHex }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={template.language.toUpperCase()} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {!template.is_active && (
                          <Chip label="Inactive" size="small" color="default" />
                        )}
                        {template.approval_status && (
                          <Chip
                            label={template.approval_status.replace('_', ' ')}
                            size="small"
                            color={getApprovalStatusColor(template.approval_status)}
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={getTemplateContent(template)}
                      >
                        {getTemplateContent(template) || '(No content)'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{template.variables.length}</TableCell>
                    <TableCell align="right">{template.usage_count || 0}×</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => handlePreview(template.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEdit(template.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={(template.usage_count || 0) > 0 ? 'In use — cannot delete' : 'Delete'}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(template)}
                              disabled={(template.usage_count || 0) > 0}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => {
            const accentHex = getChannelHex(template.channel)
            return (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
                  opacity: template.is_active ? 1 : 0.65,
                  transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
                  '&:hover': {
                    boxShadow: `0 12px 28px -8px ${accentHex}55`,
                    transform: 'translateY(-3px)',
                    borderColor: accentHex,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    bgcolor: accentHex,
                  },
                }}
              >
                <CardContent sx={{ pt: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${accentHex}1A`,
                        color: accentHex,
                        flexShrink: 0,
                        '& svg': { fontSize: 20 },
                      }}
                    >
                      {getTemplateIcon(template.channel)}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                      {template.name}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={template.channel.toUpperCase()}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: `${accentHex}1A`,
                        color: accentHex,
                      }}
                    />
                    <Chip
                      label={template.language.toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                    {!template.is_active && (
                      <Chip label="Inactive" size="small" color="default" />
                    )}
                    {template.approval_status && template.approval_status !== 'APPROVED' && (
                      <Chip
                        label={template.approval_status.replace('_', ' ')}
                        size="small"
                        color={getApprovalStatusColor(template.approval_status)}
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 1.5,
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      borderLeft: '3px solid',
                      borderLeftColor: `${accentHex}55`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {getTemplateContent(template) || '(No content)'}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="caption" color="text.disabled" fontWeight={500}>
                      Used {template.usage_count || 0}×
                    </Typography>
                    <Typography variant="caption" color="text.disabled" fontWeight={500}>
                      {template.variables.length} variables
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 1.5, pt: 0 }}>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(template.id)}
                      title="Preview"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(template.id)}
                      title="Edit"
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(template)}
                    title="Delete"
                    disabled={(template.usage_count || 0) > 0}
                    sx={{ border: '1px solid', borderColor: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
            )
          })}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{templateToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default IntegrationTemplatesPage
